import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Festival al Tramonto 2026",
        metaTitle: "Festival al Tramonto | Evento estivo in Sardegna",
        metaDescription: "Musica dal vivo, street food e tramonto sul mare. Biglietti online e informazioni chiare.",
        intro: "Musica live, street food locale e una vista che non si dimentica. Un’intera serata sul mare di Sardegna, con posti limitati.",
        menu: [
            { label: "Programma", href: "#programma" },
            { label: "Biglietti", href: "#biglietti" },
            { label: "Info", href: "#info" }
        ],
        ctaPrimary: "Acquista biglietti",
        ctaSecondary: "Info pratiche",
        startLabel: "Inizio",
        startValue: "19:00",
        startNote: "Sabato 12 luglio · Spiaggia di San Giovanni",
        shuttle: "Navetta dal centro ogni 20 minuti",
        programTitle: "Programma della serata",
        program: [
            "19:00 – Apertura cancelli e aperitivo al tramonto",
            "20:30 – Live set “Sardegna Vibes”",
            "22:00 – DJ set e area lounge sulla spiaggia"
        ],
        infoTitle: "Info pratiche",
        infoItems: [
            "Parcheggio gratuito dalle 18:00",
            "Area accessibile per famiglie",
            "Pagamenti contactless disponibili"
        ],
        tipTitle: "Consiglio",
        tipBody: "Arriva prima del tramonto per vivere tutta l’atmosfera.",
        ticketsTitle: "Biglietti",
        ticketsSubtitle: "Posti limitati. Prenota ora per assicurarti l’ingresso.",
        ticketCta: "Richiedi biglietti",
        ticketStandardTitle: "Ingresso standard",
        ticketStandardBody: "Accesso area live + drink di benvenuto.",
        ticketLoungeTitle: "Ingresso lounge",
        ticketLoungeBody: "Area riservata + 2 drink + tavolo."
    },
    en: {
        title: "Sunset Festival 2026",
        metaTitle: "Sunset Festival | Summer event in Sardinia",
        metaDescription: "Live music, street food, and a sea sunset. Tickets and clear practical info.",
        intro: "Live music, local street food and a sunset you won’t forget. A full evening by the sea in Sardinia, with limited seats.",
        menu: [
            { label: "Program", href: "#programma" },
            { label: "Tickets", href: "#biglietti" },
            { label: "Info", href: "#info" }
        ],
        ctaPrimary: "Buy tickets",
        ctaSecondary: "Practical info",
        startLabel: "Start",
        startValue: "7:00 pm",
        startNote: "Saturday 12 July · San Giovanni Beach",
        shuttle: "Shuttle from downtown every 20 minutes",
        programTitle: "Evening program",
        program: [
            "7:00 pm – Gates open and sunset aperitif",
            "8:30 pm – Live set “Sardegna Vibes”",
            "10:00 pm – DJ set and beach lounge"
        ],
        infoTitle: "Practical info",
        infoItems: [
            "Free parking from 6:00 pm",
            "Family-friendly area",
            "Contactless payments available"
        ],
        tipTitle: "Tip",
        tipBody: "Arrive before sunset to enjoy the full atmosphere.",
        ticketsTitle: "Tickets",
        ticketsSubtitle: "Limited seats. Book now to secure entry.",
        ticketCta: "Request tickets",
        ticketStandardTitle: "Standard entry",
        ticketStandardBody: "Live area access + welcome drink.",
        ticketLoungeTitle: "Lounge entry",
        ticketLoungeBody: "Reserved area + 2 drinks + table."
    },
    de: {
        title: "Sunset Festival 2026",
        metaTitle: "Sunset Festival | Sommer-Event in Sardinien",
        metaDescription: "Live-Musik, Street Food und Sonnenuntergang am Meer. Tickets und klare Infos.",
        intro: "Live-Musik, lokales Street Food und ein unvergesslicher Sonnenuntergang. Ein Abend am Meer in Sardinien, mit begrenzten Plätzen.",
        menu: [
            { label: "Programm", href: "#programma" },
            { label: "Tickets", href: "#biglietti" },
            { label: "Info", href: "#info" }
        ],
        ctaPrimary: "Tickets kaufen",
        ctaSecondary: "Infos",
        startLabel: "Beginn",
        startValue: "19:00",
        startNote: "Samstag, 12. Juli · Strand San Giovanni",
        shuttle: "Shuttle vom Zentrum alle 20 Minuten",
        programTitle: "Ablauf",
        program: [
            "19:00 – Einlass und Aperitif zum Sonnenuntergang",
            "20:30 – Live-Set “Sardegna Vibes”",
            "22:00 – DJ-Set und Beach Lounge"
        ],
        infoTitle: "Praktische Infos",
        infoItems: [
            "Kostenloses Parken ab 18:00",
            "Familienfreundlicher Bereich",
            "Kontaktloses Bezahlen möglich"
        ],
        tipTitle: "Tipp",
        tipBody: "Kommen Sie vor Sonnenuntergang, um die Stimmung voll zu genießen.",
        ticketsTitle: "Tickets",
        ticketsSubtitle: "Begrenzte Plätze. Jetzt buchen.",
        ticketCta: "Tickets anfragen",
        ticketStandardTitle: "Standard",
        ticketStandardBody: "Zugang Live-Bereich + Willkommensdrink.",
        ticketLoungeTitle: "Lounge",
        ticketLoungeBody: "Reservierter Bereich + 2 Drinks + Tisch."
    }
};

const EventoEstatePage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/evento-estivo"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-amber-900 text-white">
                <header className="relative overflow-hidden px-6 pt-10 pb-12 md:px-12 lg:px-20">
                    <div className="pointer-events-none absolute -top-24 right-10 h-72 w-72 rounded-full bg-amber-500/30 blur-3xl"></div>
                    <div className="pointer-events-none absolute -bottom-24 left-0 h-64 w-64 rounded-full bg-indigo-400/30 blur-3xl"></div>
                    <div className="relative z-10">
                        <ExampleLandingHeader title={t.title} items={t.menu} />
                        <div className="mt-8 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
                            <div>
                                <p className="text-lg text-slate-200">{t.intro}</p>
                                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                    <a href="#biglietti" className="rounded-full bg-amber-400 px-6 py-3 text-sm font-semibold text-slate-900">
                                        {t.ctaPrimary}
                                    </a>
                                    <a href="#info" className="rounded-full border border-amber-300 px-6 py-3 text-sm font-semibold text-amber-100">
                                        {t.ctaSecondary}
                                    </a>
                                </div>
                            </div>
                            <div className="rounded-3xl bg-white/10 p-6 backdrop-blur">
                                <p className="text-sm uppercase tracking-widest text-amber-200">{t.startLabel}</p>
                                <p className="mt-2 text-4xl font-semibold">{t.startValue}</p>
                                <p className="mt-3 text-sm text-slate-200">{t.startNote}</p>
                                <p className="mt-4 text-sm text-slate-200">{t.shuttle}</p>
                            </div>
                        </div>
                        <div className="mt-10 overflow-hidden rounded-3xl border border-white/10">
                            <img
                                src="https://images.unsplash.com/photo-1506157786151-b8491531f063?q=80&w=1600&auto=format&fit=crop"
                                alt="Concerto all'aperto al tramonto"
                                className="h-64 w-full object-cover md:h-96"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="programma" className="grid gap-6 lg:grid-cols-[1fr_1fr]">
                        <div className="rounded-3xl bg-white/10 p-6 backdrop-blur md:p-10">
                            <h2 className="text-2xl font-semibold">{t.programTitle}</h2>
                            <ol className="mt-6 space-y-4 text-slate-100">
                                {t.program.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ol>
                        </div>
                        <div id="info" className="rounded-3xl bg-white/95 p-6 text-slate-900 shadow-lg md:p-10">
                            <h2 className="text-2xl font-semibold">{t.infoTitle}</h2>
                            <ul className="mt-4 space-y-3 text-sm text-slate-600">
                                {t.infoItems.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                            <div className="mt-6 rounded-2xl bg-slate-100 p-4">
                                <p className="text-sm font-semibold">{t.tipTitle}</p>
                                <p className="mt-2 text-sm">{t.tipBody}</p>
                            </div>
                        </div>
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?q=80&w=1400&auto=format&fit=crop"
                            alt="Pubblico durante un concerto estivo"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1507874457470-272b3c8d8ee2?q=80&w=1400&auto=format&fit=crop"
                            alt="Luci e atmosfera da festival"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <section id="biglietti" className="mt-10 rounded-3xl bg-white/95 p-6 text-slate-900 shadow-lg md:p-10">
                        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold">{t.ticketsTitle}</h2>
                                <p className="mt-2 text-gray-700">{t.ticketsSubtitle}</p>
                            </div>
                            <a href="/estate-2026#contatto" className="rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-slate-900">
                                {t.ticketCta}
                            </a>
                        </div>
                        <div className="mt-6 grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-amber-200 p-4">
                                <p className="text-sm text-gray-600">{t.ticketStandardTitle}</p>
                                <p className="mt-2 text-2xl font-semibold">€25</p>
                                <p className="mt-2 text-sm text-gray-600">{t.ticketStandardBody}</p>
                            </div>
                            <div className="rounded-2xl border border-amber-200 p-4">
                                <p className="text-sm text-gray-600">{t.ticketLoungeTitle}</p>
                                <p className="mt-2 text-2xl font-semibold">€45</p>
                                <p className="mt-2 text-sm text-gray-600">{t.ticketLoungeBody}</p>
                            </div>
                        </div>
                    </section>
                    <SocialLinksSection className="mt-10" />
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default EventoEstatePage;
